import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Route, Routes } from 'react-router';
import { useInstallApp } from '@/hooks/useInstallApp';
import { Login } from '@/pages/Login';
import { useAuthState } from '@/lib/queries';
import { AppShell } from '@/components/AppShell';
import TodayPage from '@/pages/Today';
import InboxPage from '@/pages/Inbox';
import GoalsPage from '@/pages/Goals';
import GoalPage from '@/pages/Goal';
import ProjectPage from '@/pages/Project';
import ArchivePage from '@/pages/Archive';
import SettingsPage from '@/pages/Settings';

const SearchPage = lazy(() => import('@/pages/Search'));
const TaskPage = lazy(() => import('@/pages/Task'));
const ReviewPage = lazy(() => import('@/pages/Review'));
const StatisticsPage = lazy(() => import('@/pages/Statistics'));
const WeekPage = lazy(() => import('@/pages/Week'));

export function App() {
  const { t } = useTranslation();
  const auth = useAuthState();
  const install = useInstallApp();

  // До ответа сервера нельзя показывать ни приложение, ни форму входа:
  // и то и другое было бы догадкой о состоянии сессии.
  if (auth.isLoading) {
    return (
      <p className="p-6 text-sm text-muted" role="status">
        {t('common.loading')}
      </p>
    );
  }

  if (auth.isError) {
    return (
      <main className="flex min-h-dvh flex-col items-center justify-center gap-4 p-6">
        <h1 className="font-serif text-2xl">{t('app.name')}</h1>
        <p role="alert">{t('errors.offline')}</p>
        <Button onClick={() => void auth.refetch()}>{t('common.retry')}</Button>
      </main>
    );
  }

  if (!auth.data?.authenticated) {
    return <Login secondFactorPending={auth.data?.secondFactorPending ?? false} />;
  }

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route
          path="search"
          element={
            <Suspense fallback={<p role="status">{t('common.loading')}</p>}>
              <SearchPage />
            </Suspense>
          }
        />
        <Route
          path="tasks/:id"
          element={
            <Suspense fallback={<p role="status">{t('common.loading')}</p>}>
              <TaskPage />
            </Suspense>
          }
        />
        <Route index element={<TodayPage />} />
        <Route
          path="week"
          element={
            <Suspense fallback={<p role="status">{t('common.loading')}</p>}>
              <WeekPage />
            </Suspense>
          }
        />
        <Route
          path="review"
          element={
            <Suspense fallback={<p role="status">{t('common.loading')}</p>}>
              <ReviewPage />
            </Suspense>
          }
        />
        <Route
          path="statistics"
          element={
            <Suspense fallback={<p role="status">{t('common.loading')}</p>}>
              <StatisticsPage />
            </Suspense>
          }
        />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="goals" element={<GoalsPage />} />
        <Route path="goals/:id" element={<GoalPage />} />
        <Route path="projects/:id" element={<ProjectPage />} />
        <Route path="archive" element={<ArchivePage />} />
        <Route path="settings" element={<SettingsPage install={install} />} />
        <Route path="*" element={<TodayPage />} />
      </Route>
    </Routes>
  );
}
